import { useEffect, useMemo, useRef, useState } from 'react';
import type { RouteId, SmartSearchBrowseGroup, SmartSearchResult } from '@finance/contracts';
import { useSession } from '../../features/auth/session-provider';
import { getRouteById } from '../../routes/registry';
import { useSmartSearch } from './use-smart-search';

const SAFE_SEARCH_QUERY_PARAM_KEYS = new Set(['reportId', 'intent', 'view']);
const RECENT_ROUTES_KEY = 'finance.smart-search.recent-routes.v1';
const MAX_RECENT_ROUTES = 5;

/**
 * Resolves a server-authorized Smart Search result to an application path.
 * Only explicitly non-sensitive route-state keys may enter the URL.
 */
export function resolveSearchDestination(result: SmartSearchResult): string | null {
  if (!result.routeId) return null;
  const route = getRouteById(result.routeId as RouteId);
  if (!route) return null;

  let destination = route.path;
  if (destination.includes(':')) {
    const recordId = result.recordId;
    if (!recordId) return null;
    destination = destination.replace(/:[^/]+$/, encodeURIComponent(recordId));
  }

  if (result.queryParams && Object.keys(result.queryParams).length > 0) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(result.queryParams)) {
      if (SAFE_SEARCH_QUERY_PARAM_KEYS.has(key) && value) params.set(key, value);
    }
    const query = params.toString();
    if (query) destination = `${destination}?${query}`;
  }
  return destination;
}

function readRecentRouteIds(): string[] {
  try {
    const raw = window.sessionStorage.getItem(RECENT_ROUTES_KEY);
    const value = raw ? JSON.parse(raw) : [];
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string').slice(0, MAX_RECENT_ROUTES)
      : [];
  } catch {
    return [];
  }
}

/** Stores route-only MRU state. Query text, record IDs and account identifiers are never persisted. */
export function rememberSearchDestination(result: SmartSearchResult): void {
  if (!result.routeId || result.kind === 'RECORD') return;
  try {
    const next = [result.routeId, ...readRecentRouteIds().filter((routeId) => routeId !== result.routeId)]
      .slice(0, MAX_RECENT_ROUTES);
    window.sessionStorage.setItem(RECENT_ROUTES_KEY, JSON.stringify(next));
  } catch {
    // Session storage is an optional UX enhancement; navigation must remain unaffected.
  }
}

const BROWSE_GROUP_ORDER: readonly SmartSearchBrowseGroup[] = [
  'SUGGESTED',
  'OPERATIONS',
  'PORTFOLIO',
  'REPORTS',
  'ADMINISTRATION',
];

const BROWSE_CATEGORY: Record<SmartSearchBrowseGroup, string> = {
  SUGGESTED: 'suggested',
  OPERATIONS: 'operations',
  PORTFOLIO: 'portfolio',
  REPORTS: 'reports',
  ADMINISTRATION: 'administration',
};

export interface CommandPaletteGroup {
  key: string;
  category: string;
  items: SmartSearchResult[];
}

export interface UseCommandPaletteOptions {
  open: boolean;
  onSelect: (result: SmartSearchResult) => void;
  onClose: () => void;
}

export interface UseCommandPaletteResult {
  query: string;
  setQuery: (query: string) => void;
  activeIndex: number;
  setActiveIndex: (index: number | ((prev: number) => number)) => void;
  results: SmartSearchResult[];
  grouped: CommandPaletteGroup[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  partial: boolean;
  stale: boolean;
  retry: () => void;
  listboxId: string;
}

function authorizationCacheKey(
  user: ReturnType<typeof useSession>['user'],
  currentContext: ReturnType<typeof useSession>['currentContext'],
): string {
  return [
    user?.id ?? '_anonymous_',
    currentContext?.membershipId ?? '_membership_',
    currentContext?.organizationId ?? user?.activeOrganizationId ?? '_organization_',
    currentContext?.branchId ?? user?.activeBranchId ?? '_branch_',
    currentContext?.clientAccountId ?? user?.activeClientAccountId ?? '_client_',
    currentContext?.portfolioAccountId ?? user?.activePortfolioAccountId ?? '_portfolio_',
    user?.persona ?? '_persona_',
    user?.scopeKind ?? '_scope_',
    user?.aal ?? '_aal_',
    String(user?.entitlementsVersion ?? 0),
    user?.delegationId ?? '_delegation_',
  ].join('|');
}

export function useCommandPalette({
  open,
  onSelect,
  onClose,
}: UseCommandPaletteOptions): UseCommandPaletteResult {
  const { user, currentContext } = useSession();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentRouteIds, setRecentRouteIds] = useState<string[]>([]);
  const activeResultIdRef = useRef<string | null>(null);
  const cacheKey = useMemo(
    () => authorizationCacheKey(user, currentContext),
    [currentContext, user],
  );
  const search = useSmartSearch(query, 24, open, cacheKey);
  const queryMode = query.trim().length > 0;

  const actionableResults = useMemo(
    () => search.results.filter((result) => Boolean(resolveSearchDestination(result))),
    [search.results],
  );

  const results = useMemo(() => {
    if (queryMode) return actionableResults; // Preserve authoritative server relevance order.
    if (recentRouteIds.length === 0) return actionableResults;

    const recent: SmartSearchResult[] = [];
    const remaining: SmartSearchResult[] = [];
    for (const result of actionableResults) {
      if (result.routeId && result.kind !== 'RECORD' && recentRouteIds.includes(result.routeId)) recent.push(result);
      else remaining.push(result);
    }
    recent.sort((a, b) => recentRouteIds.indexOf(a.routeId ?? '') - recentRouteIds.indexOf(b.routeId ?? ''));
    return [...recent, ...remaining];
  }, [actionableResults, queryMode, recentRouteIds]);

  const grouped = useMemo(() => {
    if (queryMode) {
      return results.length ? [{ key: 'results', category: 'results', items: results }] : [];
    }

    const groups: CommandPaletteGroup[] = [];
    const recent = results.filter(
      (result) => result.routeId && result.kind !== 'RECORD' && recentRouteIds.includes(result.routeId),
    );
    if (recent.length) groups.push({ key: 'recent', category: 'recent', items: recent });
    const recentIds = new Set(recent.map((result) => result.id));

    for (const group of BROWSE_GROUP_ORDER) {
      const items = results.filter((result) => !recentIds.has(result.id) && result.browseGroup === group);
      if (items.length) groups.push({ key: group, category: BROWSE_CATEGORY[group], items });
    }

    const uncategorized = results.filter(
      (result) => !recentIds.has(result.id) && !result.browseGroup,
    );
    if (uncategorized.length) groups.push({ key: 'other', category: 'other', items: uncategorized });
    return groups;
  }, [queryMode, recentRouteIds, results]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    setRecentRouteIds(readRecentRouteIds());
  }, [open]);

  // Preserve keyboard selection across same-query network reconciliation by stable result identity.
  useEffect(() => {
    const previousId = activeResultIdRef.current;
    if (!previousId) {
      setActiveIndex((index) => Math.min(index, Math.max(results.length - 1, 0)));
      return;
    }
    const nextIndex = results.findIndex((result) => result.id === previousId);
    setActiveIndex(nextIndex >= 0 ? nextIndex : 0);
  }, [results]);

  useEffect(() => {
    activeResultIdRef.current = results[activeIndex]?.id ?? null;
  }, [activeIndex, results]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (query.trim().length > 0) {
          setQuery('');
          setActiveIndex(0);
        } else {
          onClose();
        }
        return;
      }

      const target = event.target;
      const isComboboxInput = target instanceof HTMLInputElement && target.getAttribute('role') === 'combobox';
      if (!isComboboxInput || results.length === 0) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, results.length - 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      }
      if (event.key === 'PageDown') {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 5, results.length - 1));
        return;
      }
      if (event.key === 'PageUp') {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 5, 0));
        return;
      }
      // Home and End intentionally remain native text-editing keys while focus is
      // on the combobox. Result options are managed through aria-activedescendant.
      if (event.key === 'Enter') {
        if (event.isComposing) return;
        const activeResult = results[activeIndex];
        if (activeResult) {
          event.preventDefault();
          onSelect(activeResult);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, results, onClose, onSelect, open]);

  return {
    query,
    setQuery,
    activeIndex,
    setActiveIndex,
    results,
    grouped,
    loading: search.loading,
    refreshing: search.refreshing,
    error: search.error,
    partial: search.partial,
    stale: search.stale,
    retry: search.retry,
    listboxId: 'command-palette-results',
  };
}
